import { useUpdate } from 'ahooks';
import React, { useEffect, useState } from 'react';
import { fetchVectorText, getVectorTextFromCache } from '@/utils/file-kit';
import { getListMarkerChars, type TextFragment } from '@/utils/text-kit';
import { type ILayerProps, type ITextLayerProps, type ListStyle } from '@/version-types';
import { createListMarkerSVGDataURL, getFontFamilySubset } from '../../utils';

type Props = {
    layer: ILayerProps;
    listMarkerFragments: TextFragment[];
};

export const SVGTypedListMarkerRenderer: React.FC<Props> = ({ layer, listMarkerFragments }) => {
    const fontFamilyMap = layer.__runtime?.fontFamilyMap || {};
    const layerData = layer.data as ITextLayerProps;
    const { fontSize, writingMode } = layerData;

    const config = layerData.listStyleMaterial!;
    const {
        url,
        autoIncrement,
        baseContentTextSize: baseFontSize,
        iconWidth: originWidth,
        iconHeight: originHeight,
        iconColors,
        fontSize: markerFontSize,
        fontColor: markerFontColor,
        padZero = false,
    } = config;

    const colorsString = JSON.stringify(iconColors);
    const ratio = fontSize / baseFontSize;
    const width = originWidth * ratio * config.scale;
    const height = originHeight * ratio * config.scale;

    const id = `list-marker-svg-typed-${layer.id}`;

    const svgStringCache = getVectorTextFromCache(url) as string;

    const updateManual = useUpdate();
    const [svgDataURL, setSVGDataURL] = useState<string | null>(null);

    const markerContainerStyle: React.CSSProperties = {
        width,
        height,
        position: 'relative',
    };

    const listOrderFontFamily = (() => {
        const fallbackFontFamily = getFontFamilySubset(layer);

        if (!layer.__listOrderFontFamily) return fallbackFontFamily;
        return fontFamilyMap[layer.__listOrderFontFamily] || fallbackFontFamily;
    })();
    const listOrderStyle: React.CSSProperties = {
        width: '100%',
        height: '100%',
        position: 'absolute',
        top: 0,
        left: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: markerFontColor,
        fontSize: markerFontSize * ratio * config.scale,
        fontFamily: listOrderFontFamily,
        writingMode,
    };

    // 加载 svg 原始字符串
    useEffect(() => {
        if (svgStringCache) return;

        fetchVectorText(url).then(() => {
            updateManual();
        });
    }, [url, svgStringCache]);

    // 创建用于渲染的 svg
    useEffect(() => {
        if (!svgStringCache) return setSVGDataURL(null);

        const result = createListMarkerSVGDataURL(svgStringCache, layer);
        setSVGDataURL(result);
    }, [svgStringCache, colorsString]);

    if (!svgDataURL) return null;

    return (
        <g data-role="list-marker">
            {listMarkerFragments.map((item) => {
                const x = item.left;
                const y = item.top;

                return (
                    <React.Fragment key={`${id}-${item.listOrder}`}>
                        <foreignObject width={width} height={height} x={x} y={y}>
                            <div style={markerContainerStyle}>
                                {/* HACK: 使用缩放解决 safari 下显示异常的问题（此时 img 要使用原始尺寸） */}
                                <div
                                    style={{
                                        width: 0,
                                        height: 0,
                                        transform: `scale(${width / originWidth}, ${height / originHeight})`,
                                    }}
                                >
                                    <img
                                        crossOrigin="anonymous"
                                        width={originWidth}
                                        height={originHeight}
                                        src={svgDataURL}
                                        style={{ display: 'block' }}
                                    />
                                </div>
                                {autoIncrement !== 'none' ? (
                                    <div style={listOrderStyle}>
                                        {getListMarkerChars(autoIncrement as ListStyle, item.listOrder!, {
                                            padZero,
                                            followDot: false,
                                        })}
                                    </div>
                                ) : null}
                            </div>
                        </foreignObject>
                    </React.Fragment>
                );
            })}
        </g>
    );
};
